import { WISH_LIST_SOURCE, pairingSourceLabel } from '../../lib/wishList'
import { Badge } from '../ui'
import {
  collectionBindingKey,
  collectionItemForRecommendation,
  layerOptionState,
  layeringUiText,
  toggleLayerOptionSelection,
} from '../../lib/layeringPicker'
import { pairingScoreView } from '../../lib/layeringScore'

export const PAIRING_NOT_IN_COLLECTION_REASON = "You don't own this."

export function pairingRenderKey(pairing) {
  if (pairing?.catalog_id) return String(pairing.catalog_id)
  return `${String(pairing?.house || '').trim().toLocaleLowerCase()}::${String(pairing?.name || '').trim().toLocaleLowerCase()}`
}

export default function PairingSuggestionList({
  pairings = [],
  selectable = false,
  anchor,
  collection = [],
  selected = [],
  onSelectedChange,
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4" data-visual-list="layering-recommendations" data-layering-pairings>
        {pairings.map(pairing => {
          const renderKey = pairingRenderKey(pairing)
          const ownedOption = pairing.source_label === WISH_LIST_SOURCE ? null : collectionItemForRecommendation(pairing, collection, anchor?.id)
          const state = ownedOption
            ? layerOptionState({ option: ownedOption, anchor, selected })
            : null
          const concentration = pairing?.concentration || ownedOption?.concentration
          const disabled = selectable && (!ownedOption || state.disabled)
          const CardSurface = selectable ? 'button' : 'article'
          const score = pairingScoreView(pairing)
          const toggle = () => {
            if (!ownedOption || !onSelectedChange) return
            const nextSelected = toggleLayerOptionSelection({ option: ownedOption, anchor, selected })
            if (nextSelected !== selected) onSelectedChange(nextSelected)
          }

          return (
            <CardSurface
              key={renderKey}
              {...(selectable ? {
                type: 'button',
                disabled,
                'aria-pressed': state?.selected || false,
                onClick: toggle,
              } : {})}
              data-list-key={renderKey}
              data-pairing-card
              data-layering-pairing-id={pairing?.catalog_id || renderKey}
              data-layering-pairing-owned={ownedOption ? 'true' : 'false'}
              data-layering-pairing-select={ownedOption?.id || undefined}
              data-collection-row-id={ownedOption?.id || undefined}
              data-collection-binding={ownedOption ? collectionBindingKey(ownedOption) : undefined}
              className={`w-full rounded-lg border p-3 text-left ${
                state?.selected
                  ? 'cursor-pointer border-gold/60 bg-gold/10 shadow-[inset_0_0_0_1px_rgba(212,175,55,0.18)]'
                  : disabled ? 'cursor-not-allowed border-white/8 bg-white/[0.02]'
                    : selectable ? 'cursor-pointer border-white/8 transition-colors hover:border-gold/40'
                      : 'border-white/8'
              }`}
            >
              <div className="mb-1 flex items-start justify-between gap-2">
                <p className="font-body text-sm font-semibold text-cream" data-pairing-name>{pairing.name}</p>
                {!selectable && pairing.role && (
                  <span data-pairing-role>
                    <Badge color="sage">{layeringUiText(pairing.role)}</Badge>
                  </span>
                )}
                {selectable && <span className="flex shrink-0 items-center gap-2">
                  {pairing.role && (
                    <span data-pairing-role>
                      <Badge color="sage">{layeringUiText(pairing.role)}</Badge>
                    </span>
                  )}
                  {state?.selected && (
                    <span
                      aria-hidden="true"
                      data-pairing-selected-check
                      className="flex h-6 w-6 items-center justify-center rounded-full bg-gold text-sm font-bold text-charcoal-900"
                    >
                      ✓
                    </span>
                  )}
                </span>}
              </div>
              <p className="mb-2 text-xs text-gold" data-source-badge>{pairingSourceLabel(pairing, ownedOption)}</p>
              <p className="mb-2 text-xs text-cream/50" data-pairing-house>{pairing.house}</p>
              {concentration && <p className="mb-2 text-[11px] text-gold/65" data-pairing-concentration>{concentration}</p>}
              <div className="mb-2" data-pairing-score={score ? `${score.display}/${score.max}` : 'unavailable'}>
                {score ? (
                  <>
                    <div className="flex items-center justify-between gap-3 text-[11px]">
                      <span className="text-cream/45">{score.label}</span>
                      <span className="font-semibold text-gold">{score.display}/{score.max}</span>
                    </div>
                    <div
                      className="mt-1 h-1 overflow-hidden rounded-full bg-white/10"
                      role="progressbar"
                      aria-label={`${score.label} score`}
                      aria-valuenow={score.raw}
                      aria-valuemin="0"
                      aria-valuemax={score.max}
                    >
                      <span className="block h-full rounded-full bg-sage" style={{ width: `${score.progress}%` }} />
                    </div>
                  </>
                ) : (
                  <span className="text-[11px] text-cream/35">Pairing score unavailable</span>
                )}
              </div>
              {pairing.reasoning && (
                <div className="rounded-lg bg-white/[0.035] px-3 py-2.5" data-pairing-why>
                  <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-cream/35">{pairing.pairing_type === 'algorithmic_accord_overlap' ? 'Recorded note match' : 'Why It Works'}</p>
                  <p className="mt-1 text-xs leading-relaxed text-cream/70" data-pairing-reason>{layeringUiText(pairing.reasoning)}</p>
                </div>
              )}
              {pairing.volatility_bridge_note && (
                <p className="mt-2 text-xs leading-relaxed text-sage">{pairing.volatility_bridge_note}</p>
              )}
              {pairing.application && <p className="mt-2 text-xs italic text-gold/70" data-pairing-application>{layeringUiText(pairing.application)}</p>}
              {!ownedOption && (
                <span className="mt-2 block text-[11px] text-amber-200" role="note">{PAIRING_NOT_IN_COLLECTION_REASON}</span>
              )}
              {state?.reason && <span className="mt-2 block text-[11px] text-amber-200" role="note">{state.reason}</span>}
            </CardSurface>
          )
        })}
      </div>
    </div>
  )
}
